import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileZipOutlined,
  LeftOutlined,
  PrinterOutlined,
  ReloadOutlined,
  SaveOutlined,
  SendOutlined,
  TableOutlined,
  UserOutlined,
  WarningTwoTone,
} from '@ant-design/icons';
import { CombinedGraphQLErrors } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import type { ValidateErrorEntity } from '@rc-component/form/lib/interface';
import {
  Alert,
  Form as AntForm,
  App,
  Button,
  DatePicker,
  Descriptions,
  Divider,
  Flex,
  Input,
  Modal,
  Space,
  Steps,
  Switch,
  Tooltip,
  theme,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate, useNavigate } from 'react-router';
import { AdditionalDataTable } from '../../components/AdditionalDataTable.tsx';
import { FormLabel } from '../../components/FormLabel';
import { LoadingIndicator } from '../../components/LoadingIndicator.tsx';
import { MainContent } from '../../components/MainContent';
import { getFragmentData } from '../../graphql/generated';
import {
  BillingType,
  type LocalizationInput,
  RequestStatus,
  type RequestSummary_SavedRequestFragment,
} from '../../graphql/generated/graphql.ts';
import { type Localization, type SelectedParameter, useAppStore } from '../../hooks/store/useAppStore';
import { useCurrentContextStore } from '../../hooks/store/useCurrentContextStore.ts';
import { useAisExport } from '../../hooks/useAisExport';
import { useLocalExport } from '../../hooks/useLocalExport.ts';
import { usePrintExtraLabels } from '../../hooks/usePrintExtraLabels.ts';
import { usePrinter } from '../../hooks/usePrinter.ts';
import { usePrintRequest } from '../../hooks/usePrintRequest';
import { useSendRequests } from '../../hooks/useSendRequests';
import { PrintButton } from '../../PrintButton.tsx';
import { htmlContent } from '../../styles/globalCss.ts';
import { API_DATETIME_FORMAT, SHORT_DATETIME_FORMAT } from '../../utils/dateFormatUtils';
import { createFormFieldValues } from '../../utils/form.ts';
import { insuranceName } from '../../utils/patient';
import { NO_PRINT_PATH_HINT } from '../../utils/print.ts';
import { hasFrontDeskRole, hasSomeRole, Role } from '../../utils/user.ts';
import { FormRenderer } from '../forms/FormRenderer';
import { CostsSummary } from './CostsSummary.tsx';
import { ParameterInfoModal } from './ParameterInfoModal';
import { PatientHeader } from './PatientHeader';
import {
  CREATE_REQUEST_MUTATION,
  REORDER_MUTATION,
  REQUEST_SUMMARY_QUERY,
  REQUEST_TRUNCATED_QUERY,
  SavedRequestFragment,
  UPDATE_REQUEST_MUTATION,
} from './RequestSummary.gql';
import { StaleRequestGuard } from './StaleRequestGuard.tsx';
import { generateLabels } from './summary/generateLabels.ts';
import { ImportantInfos } from './summary/ImportantInfos.tsx';
import { ParamList } from './summary/ParamList.tsx';
import { Specimens } from './summary/Specimens.tsx';

const { useToken } = theme;

/**
 * The store keeps more than LocalizationInput accepts, and for two different reasons.
 *
 * `freeText` is derived by the server from the Material config, so it is never sent. `fieldValues`
 * are sent for a new Anforderung, but on a Nachforderung the store holds what the *existing*
 * Etikett recorded - the chooser seeds them so the summary can show them - and the server rejects
 * them there, because that label is not ours to change.
 *
 * The explicit return type keeps the two shapes from drifting.
 */
const toLocalizationInputs = (localizations: Localization[], isReorder: boolean): LocalizationInput[] =>
  localizations.map(({ paramId, specimenId, location, fieldValues }) => ({
    paramId,
    specimenId,
    location,
    fieldValues: isReorder ? [] : fieldValues,
  }));

// fallow-ignore-next-line complexity
export const RequestSummary: React.FC = () => {
  const { token } = useToken();
  const styles = {
    flexContainer: css`
      display: flex;
      flex-direction: column;
      height: 100%;
    `,
    growZero: css`
      flex-grow: 0;
    `,
    growFull: css`
      flex-grow: 1;
      overflow: hidden;
    `,
    innerContainer: css`
      height: 100%;
      overflow-y: auto;
      padding-right: 4px; // padding to scrollbars
    `,
    actions: css`
      margin-bottom: ${token.paddingXS}px; // show button click border
    `,
  };

  type SaveAction = 'save' | 'save-print' | 'save-print-send' | 'save-print-generate-locally';

  const {
    patientData,
    paramQueue,
    formData,
    setFormData,
    labInfo,
    setLabInfo,
    acute,
    setAcute,
    elgaOptOut,
    setElgaOptOut,
    diagnose,
    setDiagnose,
    comment,
    setComment,
    requestIdForUpdate,
    requestIdForReorder,
    setRequestIdForUpdate,
    setRequestIdForReorder,
    sampledAt,
    setSampledAt,
    additionalData,
    localizations,
    aisLaunchIdForCreate,
  } = useAppStore();
  const { currentDoctorId, primaryDoctorId } = useCurrentContextStore();
  const navigate = useNavigate();
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [saveExecuting, setSaveExecuting] = useState(false);
  const [sendDisabledMessage, setSendDisabledMessage] = useState('');
  const [saveLoadingAction, setSaveLoadingAction] = useState<SaveAction | null>(null);
  const [baseForm] = useForm();
  const [formsForm] = useForm();

  const [infoParam, setInfoParam] = useState<SelectedParameter | null>(null);
  const print = usePrintRequest();
  const { canPrint } = usePrinter();
  const printExtraLabels = usePrintExtraLabels();
  const aisExport = useAisExport();
  const send = useSendRequests();
  const { generateAndExport, exportOrderZip } = useLocalExport();
  const { message, modal } = App.useApp();
  const auth = useAuth();
  const isFrontDesk = hasFrontDeskRole(auth.user);
  const localExportAllowed = hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_LAB_ADMIN], auth.user);

  const { data, loading } = useQuery(REQUEST_SUMMARY_QUERY, {
    variables: {
      doctorId: isFrontDesk ? primaryDoctorId : currentDoctorId,
      catalogParamIds: paramQueue.selectedParameters?.map(it => it.id),
      requestIdForReorder: requestIdForReorder ? requestIdForReorder : null,
    },
    skip: !patientData || !paramQueue.selectedParameters.length,
  });

  const { data: updateRequestData } = useQuery(REQUEST_TRUNCATED_QUERY, {
    variables: {
      requestId: requestIdForUpdate,
    },
    skip: !requestIdForUpdate,
  });

  const [createRequestMutation] = useMutation(CREATE_REQUEST_MUTATION);
  const [updateRequestMutation] = useMutation(UPDATE_REQUEST_MUTATION);
  const [reorderMutation] = useMutation(REORDER_MUTATION);

  const onInfo = (param: SelectedParameter) => {
    setInfoParam(param);
  };

  const saveRequest = async (action: SaveAction) => {
    let request: RequestSummary_SavedRequestFragment;
    setSaveExecuting(true);
    setSaveLoadingAction(action);

    try {
      await baseForm.validateFields();
    } catch (error) {
      baseForm.scrollToField((error as ValidateErrorEntity).errorFields[0].name, {
        block: 'center',
      });
      setSaveExecuting(false);
      setSaveLoadingAction(null);
      return;
    }

    try {
      await formsForm.validateFields();
    } catch (error) {
      formsForm.scrollToField((error as ValidateErrorEntity).errorFields[0].name, {
        block: 'center',
      });
      setSaveExecuting(false);
      setSaveLoadingAction(null);
      return;
    }

    const formFieldValues = createFormFieldValues(data?.requiredForms, formsForm.getFieldsValue(true));

    try {
      if (requestIdForUpdate) {
        const result = await updateRequestMutation({
          variables: {
            input: {
              requestId: requestIdForUpdate,
              patientData: patientData!,
              selectedParameters: paramQueue.selectedParameters.map(param => ({
                parameterId: param.id,
                parameterShortName: param.shortName,
                parameterLongName: param.longName,
                billingType: param.billingInfo.billingType,
                diagnoseId: param.billingInfo.diagnoseId,
                specialRateId: param.billingInfo.specialRateId,
              })),
              diagnose: diagnose,
              labInfo: labInfo,
              comment: comment,
              acute: acute,
              elgaOptOut: elgaOptOut,
              formFieldValues: formFieldValues,
              sampledAt: sampledAt,
              additionalData: additionalData,
              localizations: toLocalizationInputs(localizations, !!requestIdForReorder),
            },
          },
        });
        request = getFragmentData(SavedRequestFragment, result.data?.updateRequest)!;
      } else if (requestIdForReorder) {
        const result = await reorderMutation({
          variables: {
            input: {
              requestId: requestIdForReorder,
              selectedParameters: paramQueue.selectedParameters.map(param => ({
                parameterId: param.id,
                parameterShortName: param.shortName,
                parameterLongName: param.longName,
                billingType: param.billingInfo.billingType,
                diagnoseId: param.billingInfo.diagnoseId,
                specialRateId: param.billingInfo.specialRateId,
              })),
              diagnose: diagnose,
              labInfo: labInfo,
              comment: comment,
              acute: acute,
              formFieldValues: formFieldValues,
              localizations: toLocalizationInputs(localizations, !!requestIdForReorder),
            },
          },
        });
        request = getFragmentData(SavedRequestFragment, result.data!.reorder!.request)!;

        // For LOCALLY_GENERATED requests, export the new reorder files locally
        if (request.status === RequestStatus.LOCALLY_GENERATED) {
          const newOrdersCorrelationId = result.data!.reorder!.newOrdersCorrelationId;
          const newOrders = request.orders.filter(order => order.correlationId === newOrdersCorrelationId);
          for (const order of newOrders) {
            await exportOrderZip(order.id);
          }
        }
      } else {
        const result = await createRequestMutation({
          variables: {
            input: {
              doctorId: currentDoctorId,
              patientData: patientData!,
              selectedParameters: paramQueue.selectedParameters.map(param => ({
                parameterShortName: param.shortName,
                parameterLongName: param.longName,
                parameterId: param.id,
                billingType: param.billingInfo.billingType,
                diagnoseId: param.billingInfo.diagnoseId,
                specialRateId: param.billingInfo.specialRateId,
              })),
              diagnose: diagnose,
              labInfo: labInfo,
              comment: comment,
              acute: acute,
              elgaOptOut: elgaOptOut,
              formFieldValues: formFieldValues,
              sampledAt: sampledAt,
              additionalData: additionalData,
              localizations: toLocalizationInputs(localizations, !!requestIdForReorder),
              aisLaunchId: aisLaunchIdForCreate,
            },
          },
        });
        request = getFragmentData(SavedRequestFragment, result.data?.createRequest)!;
      }

      if (action === 'save-print-send' && !requestIdForReorder) {
        const response = await send([request.id], false);
        request = response.data!.sendRequests![0];
      }

      if (action === 'save-print-generate-locally' && !requestIdForReorder) {
        const requests = await generateAndExport([request.id]);
        request = requests[0];
      }

      if (action === 'save-print' || action === 'save-print-send' || action === 'save-print-generate-locally') {
        const labelsPrinted = await print(
          action === 'save-print' ? 'before-send' : 'send',
          request,
          () => message.error('Beim Drucken ist ein Fehler aufgetreten'),
          () => {}, // do nothing
          () => message.success('Etiketten & Dokumente wurden an den Drucker gesendet'),
          () => message.error('Anforderungsstatus kann nicht gesetzt werden'),
          () => {} // do nothing
        );

        if (labelsPrinted) {
          printExtraLabels(data?.doctor, patientData, request);
        }
      }

      message.success(requestIdForReorder ? 'Nachforderung wurde erfolgreich erstellt' : 'Anforderung wurde erfolgreich erstellt');
      if (action === 'save-print-send' || action === 'save-print-generate-locally') {
        await aisExport(
          request,
          [],
          msg => message.error(`Beim AIS-Export ist ein Fehler aufgetreten: ${msg}`),
          () => message.success('Dokumente für das AIS wurden erfolgreich exportiert')
        );
        navigate('/gesendete-anforderungen');
      } else {
        navigate('/offene-anforderungen');
      }
      setRequestIdForUpdate('');
      setRequestIdForReorder('');
      useAppStore.getState().setAisLaunchIdForCreate(undefined);
    } catch (e) {
      if (CombinedGraphQLErrors.is(e) && e.errors.length > 0 && e.errors[0].extensions?.classification === 'VALIDATION_ERROR') {
        setErrorModalVisible(true);
        return;
      }
      message.error('Es ist ein unbekannter Fehler beim Speichern der Anforderung aufgetreten. Bitte kontaktieren Sie den Support.');
    } finally {
      setSaveExecuting(false);
      setSaveLoadingAction(null);
    }
  };

  const handleFormChange = () => {
    setFormData(formsForm.getFieldsValue(true));
  };

  useEffect(() => {
    if (data?.requiredForms && formData) {
      formsForm.setFieldsValue(formData);
    }
  }, [formsForm, formData, data]);

  useEffect(() => {
    if (requestIdForUpdate && updateRequestData?.request?.ordersTruncated) {
      setSendDisabledMessage(
        'Multi-Labor-Anforderung: Sie haben keine Berechtigung für alle Aufträge dieser Anforderung. Die Anforderung kann dadurch nicht gesendet, sondern nur vorbereitet werden.'
      );
      return;
    }

    if (paramQueue.selectedParameters.flatMap(it => it.billingInfo.billingType).some(it => it === BillingType.DEFINE_LATER)) {
      setSendDisabledMessage(
        'Die Verrechnungsinformation mancher Parameter ist unbekannt. Die Anforderung kann dadurch nicht gesendet, sondern nur vorbereitet werden.'
      );
    }
  }, [paramQueue, requestIdForUpdate, updateRequestData]);

  const labels = useMemo(
    () => generateLabels(paramQueue.selectedParameters, localizations),
    [paramQueue.selectedParameters, localizations]
  );

  const labs = useMemo(() => {
    if (!data?.labs) {
      return [];
    }

    const labMap = new Map(data.labs.map(lab => [lab.id, lab]));
    const uniqueLabs = new Map(paramQueue.selectedParameters.map(param => [param.lab.id, labMap.get(param.lab.id)!]));

    return Array.from(uniqueLabs.values());
  }, [paramQueue, data]);

  const diagnoseRequired = useMemo(
    () =>
      labs
        .filter(it => it.diagnoseRequired)
        .some(lab => paramQueue.selectedParameters.filter(it => it.lab.id === lab.id).every(it => it.billingInfo.diagnoseId === null)),
    [labs, paramQueue]
  );

  const diagnoses = useMemo(
    () =>
      [...new Set(paramQueue.selectedParameters.filter(it => it.billingInfo.diagnoseId !== null).map(param => param.billingInfo.text))]
        .filter(it => it !== '')
        .join(', '),
    [paramQueue]
  );

  const labsWithoutElgaOptOut = useMemo(() => labs.filter(it => !it.elgaOptOut), [labs]);
  const labsWithElgaOptOut = useMemo(() => labs.filter(it => it.elgaOptOut), [labs]);

  const hasAcuteParams = useMemo(() => paramQueue.selectedParameters.some(it => it.acute) ?? false, [paramQueue]);

  if (patientData === null) {
    return <Navigate replace to="/" />;
  }

  if (!paramQueue.selectedParameters.length) {
    return <Navigate replace to="/anforderung/parameterauswahl" />;
  }

  if (loading) {
    return <LoadingIndicator />;
  }

  const insurance = insuranceName(patientData, data?.insurances ?? []);

  return (
    <MainContent size="medium" heightType="fullHeight">
      <div className={styles.flexContainer}>
        <Steps
          className={styles.growZero}
          items={[
            {
              status: 'finish',
              title: 'Patient',
              icon: <UserOutlined />,
            },
            {
              status: 'finish',
              title: 'Parameterauswahl',
              icon: <TableOutlined />,
            },
            {
              status: 'process',
              title: requestIdForUpdate
                ? 'Bestehende Anforderung abschließen'
                : requestIdForReorder
                  ? 'Nachforderung abschließen'
                  : 'Neue Anforderung abschließen',
              icon: <SendOutlined />,
            },
          ]}
        />
        <div className={styles.growFull}>
          <div className={styles.flexContainer}>
            <PatientHeader
              patientData={patientData}
              showDefaultBillingChooser={false}
              availableBillingTypes={[]}
              insuranceName={insurance}
              patientBilling={data?.doctor?.patientBilling || false}
            />
            <div className={styles.innerContainer}>
              <Space orientation="vertical" size="large" style={{ width: '100%' }}>
                <ParamList
                  params={paramQueue.selectedParameters}
                  onInfo={onInfo}
                  flipParamNames={data?.doctor?.flipParamNames ?? false}
                  limit={9}
                  showPrices={data?.doctor?.showPrices ?? true}
                />
                <ImportantInfos flipParamNames={data?.doctor?.flipParamNames ?? false} selectedParams={paramQueue.selectedParameters} />
                <Specimens
                  selectedParams={paramQueue.selectedParameters}
                  localizations={localizations}
                  labels={labels}
                  requestIdForReorder={requestIdForReorder}
                  flipParamNames={data?.doctor?.flipParamNames ?? false}
                />
                {data?.requiredForms && data.requiredForms.flatMap(it => it.fields).length > 0 && (
                  <FormRenderer formInstance={formsForm} forms={data.requiredForms} onChange={handleFormChange} />
                )}
                <AntForm form={baseForm} initialValues={{ diagnose: diagnose }}>
                  <Descriptions column={1} size="small" bordered styles={{ label: { width: '40%' } }}>
                    {hasAcuteParams && (
                      <Descriptions.Item
                        label={
                          <FormLabel
                            required={false}
                            help="Wenn aktiviert werden alle akut anforderbaren Parameter akut angefordert"
                            label={
                              <>
                                <ClockCircleOutlined /> Akut anfordern
                              </>
                            }
                            htmlFor="acute"
                          />
                        }
                      >
                        <Switch id="acute" checked={acute} onChange={checked => setAcute(checked)} />
                      </Descriptions.Item>
                    )}
                    {!requestIdForReorder && (
                      <Descriptions.Item
                        label={
                          <FormLabel
                            required={false}
                            help="Wenn Sie das Feld leer lassen, oder der ausgewählte Zeitpunkt nach dem Sendezeitpunkt liegt,
                              dann wird automatisch der Sendezeitpunkt für die Probenentnahme
                              verwendet."
                            label="Probenentnahme"
                            htmlFor="sampledAt"
                          />
                        }
                      >
                        <DatePicker
                          className={css`
                            width: 100%;
                          `}
                          id="sampledAt"
                          showTime
                          placeholder="Datum und Uhrzeit auswählen"
                          format={SHORT_DATETIME_FORMAT}
                          value={sampledAt ? dayjs(sampledAt) : null}
                          onChange={value => setSampledAt(value ? value.format(API_DATETIME_FORMAT) : null)}
                        />
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item
                      label={
                        <FormLabel
                          required={diagnoseRequired}
                          help=""
                          label="Diagnosen und Informationen für die Befundvalidierung"
                          htmlFor="diagnose"
                        />
                      }
                    >
                      <AntForm.Item
                        name="diagnose"
                        className={css`
                          margin-bottom: 0;
                        `}
                        rules={[
                          {
                            type: 'string',
                            required: diagnoseRequired,
                            whitespace: true,
                            message: 'Bitte geben Sie eine Diagnose an',
                          },
                        ]}
                      >
                        <Input.TextArea id="diagnose" value={diagnose} rows={3} onChange={e => setDiagnose(e.target.value)} />
                      </AntForm.Item>
                    </Descriptions.Item>
                    <Descriptions.Item label={<FormLabel required={false} help="" label="Informationen ans Labor" htmlFor="labInfo" />}>
                      <Input
                        id="labInfo"
                        value={labInfo}
                        onChange={e => setLabInfo(e.target.value)}
                        placeholder="z.B. klinische Angaben, Lokalisation"
                      />
                    </Descriptions.Item>
                    {!!diagnoses.length && (
                      <Descriptions.Item label="Diagnosen für die Verrechnung mit der Kasse">{diagnoses}</Descriptions.Item>
                    )}
                    <Descriptions.Item
                      label={
                        <FormLabel
                          required={false}
                          help="Diese Informationen dienen zur internen Organisation und werden nicht an das Labor übertragen"
                          label="Interner Kommentar"
                          htmlFor="comment"
                        />
                      }
                    >
                      <Input.TextArea id="comment" value={comment} rows={3} onChange={e => setComment(e.target.value)} />
                    </Descriptions.Item>
                    {!requestIdForReorder && !!labsWithElgaOptOut.length && (
                      <Descriptions.Item
                        label={
                          <FormLabel
                            required={false}
                            help="Situatives Opt-out (ELGA-Widerspruch im Anlassfall)"
                            label="ELGA Opt-out"
                            htmlFor="elgaOptOut"
                          />
                        }
                      >
                        <Switch
                          id="elgaOptOut"
                          checked={elgaOptOut}
                          onChange={checked => {
                            if (checked) {
                              modal.confirm({
                                width: 600,
                                title: 'Situatives Opt-out (ELGA-Widerspruch im Anlassfall)',
                                content: (
                                  <div>
                                    {labsWithElgaOptOut
                                      .filter(it => it.elgaOptOutText.trim().length > 0)
                                      .map((it, idx) => {
                                        return (
                                          <div key={it.name}>
                                            {idx > 0 && (
                                              <Divider
                                                className={css`
                                                  margin: ${token.marginMD}px 0;
                                                `}
                                              />
                                            )}
                                            <h3>Informationen {it.name}</h3>
                                            <div
                                              className={htmlContent}
                                              // biome-ignore lint/security/noDangerouslySetInnerHtml: DOMPUrify used
                                              dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(it.elgaOptOutText),
                                              }}
                                            />
                                          </div>
                                        );
                                      })}
                                    {!!labsWithoutElgaOptOut.length && (
                                      <Alert
                                        type="warning"
                                        className={css`
                                          margin-top: ${token.marginMD}px;
                                        `}
                                        title="Nicht alle Labors dieser Multi-Labor-Anforderung unterstützen das situative ELGA Opt-out"
                                        description={
                                          <div>
                                            <p>
                                              Folgende Labors unterstützen <strong>kein</strong> ELGA Opt-out:{' '}
                                              <strong>{labsWithoutElgaOptOut.map(it => it.name).join(',')}</strong>
                                            </p>
                                            <p
                                              className={css`
                                                margin-bottom: 0;
                                              `}
                                            >
                                              Das ELGA Opt-out wird daher ausschließlich bei folgenden Labors angewendet:{' '}
                                              <strong>{labsWithElgaOptOut.map(it => it.name).join(',')}</strong>
                                            </p>
                                          </div>
                                        }
                                      />
                                    )}
                                  </div>
                                ),
                                onOk: () => {
                                  setElgaOptOut(true);
                                },
                                okText: 'Situatives Opt-out anwenden',
                              });
                              return;
                            }
                            setElgaOptOut(false);
                          }}
                        />
                      </Descriptions.Item>
                    )}
                    {data?.doctor?.showPrices && (
                      <Descriptions.Item label="Kosten">
                        <CostsSummary
                          entries={paramQueue.selectedParameters.map(it => ({
                            billingType: it.billingInfo.billingType,
                            price: it.billingInfo.price ?? null,
                            text: it.billingInfo.text,
                            pricePatient: it.pricePatient ?? null,
                          }))}
                          showPriceSuggestion={data?.doctor?.showPriceSuggestion ?? false}
                        />
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </AntForm>
              </Space>
              {Object.keys(additionalData).length > 0 && (
                <>
                  <h3
                    className={css`
                      margin-top: ${token.paddingMD}px;
                      margin-bottom: ${token.paddingMD}px;
                      font-weight: 600;
                    `}
                  >
                    Zusatzdaten
                  </h3>
                  <AdditionalDataTable additionalData={additionalData} />
                </>
              )}
            </div>
            <Divider />
            <Flex gap="small" wrap className={styles.actions} justify="space-between">
              <Button icon={<LeftOutlined />} onClick={() => navigate('/anforderung/parameterauswahl')}>
                Zurück zur Parameterauswahl
              </Button>
              <Flex gap="small" wrap>
                {!requestIdForReorder && (
                  <>
                    <Button
                      icon={<SaveOutlined />}
                      onClick={() => saveRequest('save')}
                      disabled={saveExecuting}
                      loading={saveLoadingAction === 'save'}
                    >
                      Speichern
                    </Button>
                    <PrintButton
                      icon={<PrinterOutlined />}
                      loading={saveLoadingAction === 'save-print'}
                      onClick={() => saveRequest('save-print')}
                      disabled={saveExecuting}
                    >
                      Speichern & Drucken
                    </PrintButton>
                    {localExportAllowed && (
                      <Tooltip title={sendDisabledMessage}>
                        <Button
                          loading={saveLoadingAction === 'save-print-generate-locally'}
                          icon={<FileZipOutlined />}
                          onClick={() => saveRequest('save-print-generate-locally')}
                          disabled={!window.nativeApi || saveExecuting || !!sendDisabledMessage.length}
                        >
                          Lokal Erzeugen
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip title={sendDisabledMessage || (canPrint ? '' : NO_PRINT_PATH_HINT)}>
                      <Button
                        type="primary"
                        loading={saveLoadingAction === 'save-print-send'}
                        icon={<CheckCircleOutlined />}
                        onClick={() => saveRequest('save-print-send')}
                        disabled={!canPrint || saveExecuting || !!sendDisabledMessage.length}
                      >
                        Speichern, Drucken & Senden
                      </Button>
                    </Tooltip>
                  </>
                )}
                {requestIdForReorder && (
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => saveRequest('save-print-send')}
                    disabled={saveExecuting}
                  >
                    Nachforderung abschließen
                  </Button>
                )}
              </Flex>
            </Flex>
          </div>
        </div>
      </div>
      <StaleRequestGuard saving={saveExecuting} />
      <ParameterInfoModal
        parameter={infoParam}
        onClose={() => setInfoParam(null)}
        flipParamNames={data?.doctor?.flipParamNames ?? false}
        showPrices={data?.doctor?.showPrices ?? true}
      />
      <Modal
        title={
          <>
            <WarningTwoTone twoToneColor="#f5222d" /* red-6 */ /> Die Anforderung kann nicht abgeschlossen werden
          </>
        }
        open={errorModalVisible}
        closable={false}
        keyboard={false}
        maskClosable={false}
        cancelButtonProps={{ hidden: true }}
        okText="Vorgang erneut starten"
        okButtonProps={{ icon: <ReloadOutlined /> }}
        onOk={() => navigate('/anforderung')}
      >
        <p>Bei der Validierung der anzufordernden Parameter wurde ein Problem gefunden.</p>
        <p>
          Möglicherweise wurden die Parameterdefinitionen während Ihrer Anforderungserstellung geändert, weshalb Sie die Anforderung nicht
          erstellen können.
        </p>
        <p>Bitte wiederholen Sie den Vorgang. Wenden Sie sich bitte an unseren Support, falls das Problem weiterhin bestehen sollte.</p>
        <Alert
          type="info"
          showIcon
          title="Tipp: Schließen Sie den Prozess der Anforderungserstellung so schnell wie möglich ab. Zu lange geöffnete Prozesse können diese Meldung verursachen."
        />
      </Modal>
    </MainContent>
  );
};
